import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const slideKeys = [
  {
    id: 1,
    tagKey: 'hero.slide1.tag',
    titleKey: 'hero.slide1.title',
    subtitleKey: 'hero.slide1.subtitle',
    descKey: 'hero.slide1.desc',
    cta1Key: 'hero.slide1.cta1',
    cta2Key: 'hero.slide1.cta2',
    cta1Href: '#contact',
    cta2Href: '#cases',
    bg: 'https://readdy.ai/api/search-image?query=abstract%20dark%20teal%20navy%20blue%20phoenix%20rising%20digital%20technology%20background%20with%20golden%20light%20rays%20and%20elegant%20feather%20patterns%2C%20deep%20ocean%20color%20gradient%2C%20mystical%20atmosphere%2C%20high%20contrast%20dramatic%20lighting%2C%20ultra%20wide%20cinematic%20composition%2C%20professional%20corporate%20aesthetic&width=1440&height=800&seq=hero1&orientation=landscape',
  },
  {
    id: 2,
    tagKey: 'hero.slide2.tag',
    titleKey: 'hero.slide2.title',
    subtitleKey: 'hero.slide2.subtitle',
    descKey: 'hero.slide2.desc',
    cta1Key: 'hero.slide2.cta1',
    cta2Key: 'hero.slide2.cta2',
    cta1Href: '#services',
    cta2Href: '#contact',
    bg: 'https://readdy.ai/api/search-image?query=modern%20sleek%20website%20design%20on%20multiple%20devices%20laptop%20tablet%20phone%20mockup%2C%20warm%20cream%20ivory%20background%20with%20golden%20amber%20accents%2C%20professional%20digital%20workspace%2C%20clean%20minimal%20aesthetic%2C%20soft%20warm%20lighting%2C%20high%20end%20tech%20product%20photography%20style&width=1440&height=800&seq=hero2&orientation=landscape',
  },
  {
    id: 3,
    tagKey: 'hero.slide3.tag',
    titleKey: 'hero.slide3.title',
    subtitleKey: 'hero.slide3.subtitle',
    descKey: 'hero.slide3.desc',
    cta1Key: 'hero.slide3.cta1',
    cta2Key: 'hero.slide3.cta2',
    cta1Href: '#cases',
    cta2Href: '#contact',
    bg: 'https://readdy.ai/api/search-image?query=elegant%20mobile%20app%20interface%20design%20on%20smartphone%20screens%2C%20dark%20teal%20navy%20background%20with%20warm%20golden%20glow%2C%20futuristic%20digital%20technology%20concept%2C%20abstract%20light%20particles%2C%20premium%20luxury%20tech%20aesthetic%2C%20cinematic%20wide%20angle%20composition&width=1440&height=800&seq=hero3&orientation=landscape',
  },
];

export default function HeroCarousel() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((idx: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 300);
  }, [animating]);

  const prev = () => goTo((current - 1 + slideKeys.length) % slideKeys.length);
  const next = useCallback(() => goTo((current + 1) % slideKeys.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5500);
    return () => clearInterval(timer);
  }, [next]);

  const handleCta = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const slide = slideKeys[current];

  return (
    <section id="home" className="relative w-full h-[100vh] min-h-[600px] overflow-hidden">
      {slideKeys.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === current ? 'opacity-100' : 'opacity-0'}`}
        >
          <img src={s.bg} alt={t(s.titleKey)} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/40 to-black/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
          <div
            className={`max-w-2xl transition-all duration-500 ${
              animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/40 mb-6">
              <img
                src="https://static.readdy.ai/image/d423d7259cd6886cdb573e095a6b9999/a48c44a5812fde7aa1b743301ff8f67a.png"
                alt="logo"
                className="h-4 w-4 object-contain brightness-200"
              />
              <span className="text-amber-300 text-xs font-medium tracking-wider">{t(slide.tagKey)}</span>
            </div>

            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t(slide.titleKey)}
            </h1>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-amber-400 leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t(slide.subtitleKey)}
            </h2>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-10 max-w-xl">
              {t(slide.descKey)}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => handleCta(slide.cta1Href)}
                className="px-8 py-3.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t(slide.cta1Key)}
              </button>
              <button
                onClick={() => handleCta(slide.cta2Href)}
                className="px-8 py-3.5 rounded-full border-2 border-white/60 hover:border-white text-white font-semibold text-sm transition-all duration-200 whitespace-nowrap cursor-pointer hover:bg-white/10"
              >
                {t(slide.cta2Key)}
              </button>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white transition-all duration-200 cursor-pointer"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white transition-all duration-200 cursor-pointer"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {slideKeys.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current ? 'w-8 h-2 bg-amber-400' : 'w-2 h-2 bg-white/40 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      <div className="absolute bottom-8 right-8 md:right-12 z-20 flex flex-col items-center gap-2 opacity-60">
        <span className="text-white text-xs tracking-widest rotate-90 origin-center">SCROLL</span>
        <div className="w-px h-10 bg-white/50"></div>
      </div>
    </section>
  );
}
